import axios from "axios";
import {
  clearPatientWebSession,
  isPatientWebAuthPath,
  loadPatientWebSession,
  markPatientClinicalConsentRequired,
  savePatientWebSession,
} from './patientWebAuth';

// 環境変数からAPI URLを取得
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL 
  || process.env.REACT_APP_API_DOMEIN 
  || (process.env.NODE_ENV === 'production' 
      ? 'https://api.curenow.jp' 
      : 'http://localhost:4000');

if (process.env.NODE_ENV !== 'production') {
  console.log('[Axios] API_BASE_URL:', API_BASE_URL);
}

const DEFAULT_ERROR_MESSAGES = {
  network: '通信に失敗しました。通信環境をご確認のうえ、再度お試しください。',
  400: '入力内容をご確認ください。',
  401: '認証に失敗しました。再度お試しください。',
  403: 'この操作は実行できません。',
  404: '対象が見つかりませんでした。',
  409: '処理が競合しました。少し時間をおいて再度お試しください。',
  422: '入力内容をご確認ください。',
  500: 'しばらくしてから再度お試しください。',
};

const normalizeErrorText = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^Request failed with status code \d+$/i.test(trimmed)) return '';
  return trimmed;
};

const pickFirstMessage = (values = []) => values.map(normalizeErrorText).find(Boolean) || '';

export const extractApiErrorMessage = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const responseErrors = Array.isArray(data?.errors) ? data.errors : [];

  const nestedMessages = responseErrors.flatMap((item) => {
    if (typeof item === 'string') return [item];
    return [item?.error_message, item?.message, item?.detail];
  });

  const message = pickFirstMessage([
    data?.message,
    data?.error,
    data?.detail,
    ...nestedMessages,
    error?.message,
  ]);

  if (message) return message;
  if (!error?.response) return DEFAULT_ERROR_MESSAGES.network;
  if (status >= 500) return DEFAULT_ERROR_MESSAGES[500];
  return DEFAULT_ERROR_MESSAGES[status] || '処理に失敗しました。時間をおいて再度お試しください。';
};

const shouldEmitGlobalError = (error) => {
  const config = error?.config || {};
  const url = config?.url || '';

  if (config.skipGlobalError === true) return false;
  if (url.includes('/auth/liff')) return false;
  return true;
};

const emitGlobalError = (error) => {
  if (typeof window === 'undefined' || !shouldEmitGlobalError(error)) return;

  try {
    window.dispatchEvent(new CustomEvent('medichat:api-error', {
      detail: {
        message: extractApiErrorMessage(error),
        status: error?.response?.status ?? null,
        url: error?.config?.url || '',
        method: (error?.config?.method || 'get').toUpperCase(),
      }
    }));
  } catch (_) {}
};

export const showUserFacingError = ({
  message,
  title = '操作に失敗しました',
  iconType = 'error',
} = {}) => {
  if (typeof window === 'undefined') return;
  if (!normalizeErrorText(message)) return;

  try {
    window.dispatchEvent(new CustomEvent('medichat:user-error', {
      detail: {
        title,
        message: normalizeErrorText(message),
        iconType,
      }
    }));
  } catch (_) {}
};

export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

// グローバルローディング（全API通信前後でイベントを発火）
let __inflightRequests = 0;
// チャット内の設問送信・戻る・選択系はオーバーレイを出さない。
// 二重送信は各画面の isSubmitting でガードし、次コンテンツは silent refresh で差し替える。
// ただし疾患/科目選択の確定は通信待ちが長いためオーバーレイを許可する
// （specialty タップの並べ替え等は呼び出し側で skipGlobalLoading: true）。
export const CHAT_INTERACTION_URL_RE =
  /\/api\/medichat\/sessions\/[^/]+\/(questionnaire(?:\/|$)|back(?:$|\?)|back_to_|select_|choose_|confirm_|upload_|start_|complete_|delivery\/)/;
export const CHAT_FORCE_LOADING_URL_RE =
  /\/api\/medichat\/sessions\/[^/]+\/(choose_department_entry|choose_selection_entry|select_diseases|select_symptoms)(?:$|\?)/;
export const shouldTrackGlobalLoading = (config = {}) => {
  if (config?.skipGlobalLoading === true) return false;
  const url = String(config?.url || '');
  if (CHAT_FORCE_LOADING_URL_RE.test(url)) return true;
  if (CHAT_INTERACTION_URL_RE.test(url)) return false;
  return true;
};
const requestNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const emitApiLatency = (config, durationMs, status) => {
  try {
    const method = (config?.method || 'get').toUpperCase();
    const url = config?.url || '';
    window.dispatchEvent(new CustomEvent('medichat:api-latency', {
      detail: {
        method,
        url,
        status: status ?? null,
        duration_ms: Math.round(durationMs),
      }
    }));
  } catch (_) {}
};
const emitGlobalLoading = (delta) => {
  try {
    __inflightRequests = Math.max(0, __inflightRequests + delta);
    window.dispatchEvent(new CustomEvent('global:loading', { detail: { count: __inflightRequests } }));
  } catch (_) {}
};

let patientRefreshPromise = null;

export const shouldRecoverLinePatientSession = ({
  status,
  requestUrl,
  pathname,
  hasWebSession,
  lineToken,
}) => (
  status === 401
  && !hasWebSession
  && !String(pathname || '').startsWith('/doctor')
  && !String(requestUrl || '').includes('/auth/')
  && Boolean(lineToken)
);

export const refreshPatientWebSession = async () => {
  const initialSession = loadPatientWebSession();
  if (!initialSession?.access_token) return null;

  const performRefresh = async () => {
    const latestSession = loadPatientWebSession();
    if (!latestSession?.access_token) return null;
    if (latestSession.access_token !== initialSession.access_token) return latestSession;

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/medichat/auth/refresh`,
        {},
        {
          timeout: 30000,
          withCredentials: true,
          headers: { 'X-Medichat-Client': 'patient-web' },
        },
      );
      savePatientWebSession(data);
      return data;
    } catch (error) {
      if (error?.response?.status !== 401) throw error;

      const currentSession = loadPatientWebSession();
      if (
        currentSession?.access_token
        && currentSession.access_token !== initialSession.access_token
      ) {
        return currentSession;
      }

      clearPatientWebSession();
      try {
        window.dispatchEvent(new CustomEvent('medichat:patient-auth-expired'));
      } catch (_) {}
      return null;
    }
  };

  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request('medichat-patient-refresh', { mode: 'exclusive' }, performRefresh);
  }
  return performRefresh();
};

axiosInstance.interceptors.request.use((config) => {
  config.metadata = {
    ...(config.metadata || {}),
    requestStartedAt: requestNow(),
  };
  if (shouldTrackGlobalLoading(config)) emitGlobalLoading(+1);
  return config;
}, (error) => {
  if (shouldTrackGlobalLoading(error?.config)) emitGlobalLoading(-1);
  return Promise.reject(error);
});

axiosInstance.interceptors.response.use((response) => {
  if (shouldTrackGlobalLoading(response?.config)) emitGlobalLoading(-1);
  const startedAt = response?.config?.metadata?.requestStartedAt;
  if (startedAt) {
    emitApiLatency(response?.config, requestNow() - startedAt, response?.status);
  }
  return response;
}, async (error) => {
  if (shouldTrackGlobalLoading(error?.config)) emitGlobalLoading(-1);
  const startedAt = error?.config?.metadata?.requestStartedAt;
  if (startedAt) {
    emitApiLatency(error?.config, requestNow() - startedAt, error?.response?.status);
  }

  const request = error?.config;
  if (
    error?.response?.status === 403
    && error?.response?.data?.error === 'clinical_email_consent_required'
  ) {
    markPatientClinicalConsentRequired();
    return Promise.reject(error);
  }

  const hadWebSessionAtFailure = Boolean(loadPatientWebSession());
  let patientWebSessionExpired = false;
  const canRefresh =
    request
    && error?.response?.status === 401
    && request._patientAuthRetry !== true
    && !String(request.url || '').includes('/api/medichat/auth/')
    && loadPatientWebSession()?.access_token;

  if (canRefresh) {
    request._patientAuthRetry = true;
    patientRefreshPromise ||= refreshPatientWebSession();
    const refreshed = await patientRefreshPromise.finally(() => {
      patientRefreshPromise = null;
    });
    if (refreshed?.access_token) {
      request.headers = request.headers || {};
      request.headers.Authorization = `Bearer ${refreshed.access_token}`;
      return axiosInstance.request(request);
    }
    patientWebSessionExpired = true;
  }

  const hasLegacyPatientSession = shouldRecoverLinePatientSession({
    status: error?.response?.status,
    requestUrl: request?.url,
    pathname: window.location.pathname,
    hasWebSession: hadWebSessionAtFailure || Boolean(loadPatientWebSession()),
    lineToken: localStorage.getItem('medichat_auth_token'),
  });
  if (hasLegacyPatientSession) {
    try {
      window.dispatchEvent(new CustomEvent('medichat:line-auth-expired'));
    } catch (_) {}
  }

  if (!patientWebSessionExpired) emitGlobalError(error);
  return Promise.reject(error);
});

// リクエストインターセプター：AuthorizationヘッダーとCSRFトークンを自動追加
axiosInstance.interceptors.request.use(
  (config) => {
    // localStorageから認証トークンを取得
    const webSession = loadPatientWebSession();
    const authToken = webSession?.access_token || localStorage.getItem('medichat_auth_token');
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    if (
      loadPatientWebSession()?.access_token
      || isPatientWebAuthPath(window.location.pathname)
    ) {
      config.headers['X-Medichat-Client'] = 'patient-web';
    }

    // CSRFトークンを追加（POST/PUT/DELETE/PATCHリクエストの場合）
    if (['post', 'put', 'delete', 'patch'].includes(config.method)) {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
